import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { PetGameObject } from "$/game/prefabs/PetGameObject.ts";
import { SoapInteractiveObject } from "$/game/prefabs/home/SoapInteractiveObject.ts";
import { petActions } from "$/game/emitters.ts";

export class CleanPetSystem {
  private cleaningStartTime: number = 0;
  private isCleaning: boolean = false;
  private bubbleTimer?: Phaser.Time.TimerEvent;
  private bubbles: Phaser.GameObjects.Arc[] = [];
  private soundTimer?: Phaser.Time.TimerEvent;

  constructor(
    private myScene: BasicScene,
    private pet: PetGameObject,
    private soap: SoapInteractiveObject,
  ) {
  }

  public update(): void {
    // Проверяем, перетаскивается ли мыло
    if (!this.soap.dragging) {
      // Если мыло не перетаскивается, сбрасываем состояние очистки
      if (this.isCleaning) {
        this.stopCleaning();
      }
      return;
    }

    // Проверяем, находится ли мыло над питомцем
    const isOverPet = this.isSoapOverPet();

    if (isOverPet) {
      if (!this.isCleaning) {
        // Начинаем процесс очистки
        this.startCleaning();
      }
    } else {
      // Если мыло не над питомцем, сбрасываем таймер
      if (this.isCleaning) {
        this.stopCleaning();
      }
    }

    // Если идет процесс очистки, проверяем, прошло ли достаточно времени
    if (this.isCleaning) {
      const config = this.myScene.global.TUNER_TWA_CONFIG.game.systems.cleaning;
      const elapsed = this.myScene.time.now - this.cleaningStartTime;
      if (elapsed >= config.duration) {
        this.completeCleaning();
      }
    }
  }

  private isSoapOverPet(): boolean {
    // Получаем границы объектов
    const soapBounds = this.soap.getBounds();
    const petBounds = this.pet.getBounds();

    // Проверяем пересечение прямоугольников
    return Phaser.Geom.Rectangle.Overlaps(soapBounds, petBounds);
  }

  private startCleaning(): void {
    this.isCleaning = true;
    this.cleaningStartTime = this.myScene.time.now;

    this.myScene.telegram.twaSDK.hapticFeedback.impactOccurred("light");

    // Play sound using soundManager (audiosprite)
    this.playBubblesSound();

    // Repeat sound every 800ms to simulate looping
    this.soundTimer = this.myScene.time.addEvent({
      delay: 800,
      callback: this.playBubblesSound,
      callbackScope: this,
      loop: true,
    });

    // Запускаем генерацию пузырей
    this.startBubbleGeneration();
  }

  private playBubblesSound(): void {
    if (this.isCleaning) {
      this.myScene.soundManager.playSound(
        this.myScene.keys.assets.sounds.bubblesPopping,
        { volume: 0.5 },
      );
    }
  }

  private stopCleaning(): void {
    this.isCleaning = false;
    this.cleaningStartTime = 0;

    if (this.soundTimer) {
      this.soundTimer.destroy();
      this.soundTimer = undefined;
    }

    // Останавливаем генерацию пузырей
    this.stopBubbleGeneration();
  }

  private completeCleaning(): void {
    this.myScene.api.pet.useSoap();
    petActions.soapUsed();
    console.log("Cleaning pet completed!");
    this.myScene.telegram.twaSDK.hapticFeedback.notificationOccurred("success");
    this.stopCleaning();
  }

  private startBubbleGeneration(): void {
    // Очищаем существующие пузыри
    this.clearBubbles();

    // Создаем таймер для генерации пузырей каждые 200мс
    this.bubbleTimer = this.myScene.time.addEvent({
      delay: 150,
      callback: this.createBubble,
      callbackScope: this,
      loop: true,
    });
  }

  private stopBubbleGeneration(): void {
    if (this.bubbleTimer) {
      this.bubbleTimer.destroy();
      this.bubbleTimer = undefined;
    }

    // Очищаем все пузыри через некоторое время
    this.myScene.time.delayedCall(3000, () => {
      this.clearBubbles();
    });
  }

  private createBubble(): void {
    if (!this.isCleaning) return;

    // Генерируем пузырь в случайной позиции рядом с мылом
    const soapX = this.soap.x;
    const soapY = this.soap.y;
    const offsetX = Phaser.Math.Between(-30, 30);
    const offsetY = Phaser.Math.Between(-30, 30);

    const bubble = this.myScene.add.circle(
      soapX + offsetX,
      soapY + offsetY,
      Phaser.Math.Between(20, 40),
      0xffffff,
      1,
    );

    // Анимация пузыря: поднимается вверх и исчезает
    this.myScene.tweens.add({
      targets: bubble,
      y: bubble.y - Phaser.Math.Between(50, 100),
      x: bubble.x + Phaser.Math.Between(-20, 20),
      alpha: 0,
      scale: Phaser.Math.FloatBetween(1.2, 2),
      duration: Phaser.Math.Between(1200, 1800),
      ease: "Power1",
      onComplete: () => {
        bubble.destroy();
        const index = this.bubbles.indexOf(bubble);
        if (index > -1) {
          this.bubbles.splice(index, 1);
        }
      },
    });

    this.bubbles.push(bubble);
  }

  private clearBubbles(): void {
    this.bubbles.forEach((bubble) => {
      if (bubble && bubble.active) {
        bubble.destroy();
      }
    });
    this.bubbles = [];
  }

  public destroy(): void {
    if (this.soundTimer) {
      this.soundTimer.destroy();
      this.soundTimer = undefined;
    }

    // Останавливаем генерацию пузырей
    this.stopBubbleGeneration();

    // Очищаем пузыри
    this.clearBubbles();
  }
}
